<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useElementBounding } from '@vueuse/core';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
    validator: value =>
      value.every(option => 'value' in option && 'label' in option),
  },
  placeholder: { type: String, default: '' },
  // Fallback label shown when the selected value is not in `options` yet
  // (e.g. API-backed lists that load lazily on open).
  displayLabel: { type: String, default: '' },
  modelValue: { type: [String, Number], default: '' },
  disabled: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: '' },
  emptyState: { type: String, default: '' },
  message: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
  useApiResults: { type: Boolean, default: false }, // useApiResults prop to determine if search is handled by API
  teleportToBody: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'search', 'open']);

const { t } = useI18n();

const selectedValue = ref(props.modelValue);
const open = ref(false);
const search = ref('');
const dropdownRef = ref(null);
const comboboxRef = ref(null);
const dropdownElement = computed(() => dropdownRef.value?.element || null);
// Native modal dialogs make body siblings inert. Keep the popup inside its form.
const teleportTarget = computed(() => {
  if (!open.value) return 'body';
  const dialog = comboboxRef.value?.closest('dialog');
  return dialog ? comboboxRef.value.closest('form') || dialog : 'body';
});
const popupContainer = computed(() =>
  typeof teleportTarget.value === 'string' ? null : teleportTarget.value
);
const popupBounds = useElementBounding(popupContainer);
const floatingEnabled = computed(() => props.teleportToBody && open.value);

const { fixedPosition, updatePosition } = useDropdownPosition(
  comboboxRef,
  dropdownElement,
  floatingEnabled,
  { align: 'start' }
);

const floatingStyle = computed(() => {
  const style = { ...fixedPosition.value.style };
  // The dialog form's transform/backdrop-filter establishes a fixed-position
  // containing block. Convert viewport coordinates to its padding box.
  const container = popupContainer.value;
  const containerStyle = container ? getComputedStyle(container) : null;
  const containsFixedPopup =
    containerStyle &&
    [
      containerStyle.transform,
      containerStyle.filter,
      containerStyle.backdropFilter,
    ].some(value => value && value !== 'none');
  if (containsFixedPopup) {
    const offsets = {
      left:
        popupBounds.left.value + container.clientLeft - container.scrollLeft,
      top: popupBounds.top.value + container.clientTop - container.scrollTop,
      right:
        window.innerWidth -
        popupBounds.left.value -
        container.clientLeft -
        container.clientWidth +
        container.scrollLeft,
      bottom:
        window.innerHeight -
        popupBounds.top.value -
        container.clientTop -
        container.clientHeight +
        container.scrollTop,
    };
    Object.entries(offsets).forEach(([edge, offset]) => {
      if (style[edge]) style[edge] = `${parseFloat(style[edge]) - offset}px`;
    });
  }
  return {
    ...style,
    width: comboboxRef.value ? `${comboboxRef.value.offsetWidth}px` : undefined,
  };
});

const filteredOptions = computed(() => {
  // For API search, don't filter options locally
  if (props.useApiResults && search.value) {
    return props.options;
  }

  // For local search, filter options based on search term
  const searchTerm = search.value.toLowerCase();
  return props.options.filter(option =>
    option.label.toLowerCase().includes(searchTerm)
  );
});
const selectPlaceholder = computed(() => {
  return props.placeholder || t('COMBOBOX.PLACEHOLDER');
});
const selectedOption = computed(() =>
  props.options.find(option => option.value === selectedValue.value)
);
const selectedIcon = computed(() => selectedOption.value?.icon || '');
const selectedLabel = computed(() => {
  const selected = props.options.find(
    option => option.value === selectedValue.value
  );
  return selected?.label ?? (props.displayLabel || selectPlaceholder.value);
});

const selectOption = option => {
  if (selectedValue.value === option.value) {
    selectedValue.value = '';
    emit('update:modelValue', '');
  } else {
    selectedValue.value = option.value;
    emit('update:modelValue', option.value);
  }
  open.value = false;
  search.value = '';
};

const toggleDropdown = () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    emit('open');
    nextTick(() => {
      popupBounds.update();
      updatePosition();
      dropdownRef.value?.focus();
    });
  }
};

const handleClickOutside = event => {
  if (dropdownRef.value?.element?.contains(event.target)) return;
  open.value = false;
};

watch(
  () => props.modelValue,
  newValue => {
    selectedValue.value = newValue;
  }
);
</script>

<template>
  <div
    ref="comboboxRef"
    class="relative w-full min-w-0"
    :class="{
      'cursor-not-allowed': disabled,
      'group/combobox': !disabled,
    }"
    @click.prevent
  >
    <OnClickOutside @trigger="handleClickOutside">
      <Button
        variant="outline"
        :color="hasError && !open ? 'ruby' : open ? 'blue' : 'slate'"
        :label="selectedLabel"
        trailing-icon
        :disabled="disabled"
        no-animation
        class="justify-between w-full !px-3 !py-2.5 text-n-slate-12 font-normal group-hover/combobox:border-n-slate-6 focus:outline-n-brand"
        :class="{
          focused: open,
          '[&:not(.focused)]:dark:outline-[rgb(234_234_234_/_0.2)] [&:not(.focused)]:hover:enabled:outline-n-slate-6 [&:not(.focused)]:dark:hover:enabled:outline-n-slate-6':
            !hasError,
        }"
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click="toggleDropdown"
      >
        <template v-if="selectedIcon" #default>
          <span class="flex min-w-0 flex-1 items-center gap-2">
            <Icon
              :icon="selectedIcon"
              class="size-4 flex-shrink-0"
              :class="selectedOption?.iconClass || 'text-n-slate-11'"
              :title="selectedOption?.iconLabel || undefined"
            />
            <span class="button-label min-w-0 truncate">{{
              selectedLabel
            }}</span>
          </span>
        </template>
      </Button>

      <Teleport :to="teleportTarget" :disabled="!teleportToBody">
        <ComboBoxDropdown
          ref="dropdownRef"
          v-model:search-value="search"
          :open="open"
          :options="filteredOptions"
          :search-placeholder="searchPlaceholder"
          :empty-state="emptyState"
          :selected-values="selectedValue"
          :floating="teleportToBody"
          :floating-class="fixedPosition.class"
          :floating-style="floatingStyle"
          @search="emit('search', $event)"
          @select="selectOption"
        />
      </Teleport>

      <p
        v-if="message"
        class="mt-2 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
        :class="{
          'text-n-ruby-9': hasError,
          'text-n-slate-11': !hasError,
        }"
      >
        {{ message }}
      </p>
    </OnClickOutside>
  </div>
</template>
